import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { CARD_SIZES, renderChartCard, shareImage, type CardSize, type ChartCard } from "../lib/share";

/**
 * "Share" on a chart: pick a size, and the card goes to the phone's share sheet or downloads as a
 * PNG with the caption and link copied. "Copy link" hands over just the game's URL.
 */
export function ShareButton({
  card,
  caption,
  url,
  filename,
  disabled,
}: {
  /** Built on demand, so the image always matches what's on screen. */
  card: () => ChartCard | null;
  caption: string;
  url: string;
  filename: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const root = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [open]);

  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 3500);
    return () => clearTimeout(t);
  }, [note]);

  const exportAs = async (size: CardSize) => {
    const data = card();
    if (!data) return;
    setBusy(true);
    try {
      const blob = await renderChartCard(data, size);
      const outcome = await shareImage(blob, { filename, caption, url });
      if (outcome === "downloaded") setNote("Image saved · caption and link copied");
      else if (outcome === "shared") setNote("Shared");
    } catch {
      setNote("Couldn't make the image");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard?.writeText(url).catch(() => null);
    setNote("Link copied");
    setOpen(false);
  };

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || busy}
        aria-expanded={open}
        className={cn(
          "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-40",
          open ? "border-accent text-ink" : "border-line text-dim hover:border-cell-hi hover:text-ink",
        )}
      >
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M8 10V2.5M5 5.5l3-3 3 3M3 9v3.5A1.5 1.5 0 004.5 14h7a1.5 1.5 0 001.5-1.5V9"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {busy ? "Making image…" : "Share"}
      </button>

      {open && (
        <div className="fade-in absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-2xl border border-line bg-panel p-1.5 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.7)]">
          {(Object.keys(CARD_SIZES) as CardSize[]).map((size) => (
            <MenuItem key={size} onClick={() => exportAs(size)}>
              <span>Image for {CARD_SIZES[size].label}</span>
              <span className="text-[11px] text-faint">
                {CARD_SIZES[size].w}×{CARD_SIZES[size].h}
              </span>
            </MenuItem>
          ))}
          <MenuItem onClick={copyLink}>
            <span>Copy link</span>
          </MenuItem>
        </div>
      )}

      {note && (
        <div
          role="status"
          className="fade-in absolute right-0 z-20 mt-2 whitespace-nowrap rounded-full border border-line bg-panel px-3 py-1.5 text-[12px] text-ink"
        >
          {note}
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-[13px] font-semibold text-ink transition-colors hover:bg-cell"
    >
      {children}
    </button>
  );
}
